import { useCallback, useMemo } from "react"
import { ApplicationListItem } from "../../dtos/ApplicantListItem"
import {
    GetAllApplicantsForVacancyId,
    createExpertInterview,
    createScreeningCall,
    createVideoInterview as createVideoInterviewApi,
    getQuestions as getQuestionsApi,
} from "../../applicants-service"
import { useSelector } from "react-redux"
import { selectCurrentOrganization } from "@/modules/organizations/store/organizationSlice"
import { CreateExpertInterviewCommand } from "../../dtos/CreateExpertInterviewCommand"
import { CreateVideoInterviewCommand } from "../../dtos/CreateVideoInterviewCommand"

export function useApplicants() {
    const currentOrganization = useSelector(selectCurrentOrganization)
    const organizationId = currentOrganization?.id

    const getApplicants = useCallback(
        (vacancyId: string): Promise<ApplicationListItem[]> => {
            return GetAllApplicantsForVacancyId(organizationId ?? "", vacancyId)
        },
        [organizationId],
    )

    const createInterview = useCallback(
        async (applicationId: string) => {
            if (!organizationId) return
            return await createScreeningCall(organizationId, applicationId)
        },
        [organizationId],
    )

    const scheduleExpertInterview = useCallback(
        async (command: CreateExpertInterviewCommand) => {
            if (!organizationId) return
            return await createExpertInterview(organizationId, command)
        },
        [organizationId],
    )

    const createVideoInterview = useCallback(
        async (command: CreateVideoInterviewCommand) => {
            if (!organizationId) return
            return await createVideoInterviewApi(organizationId, command)
        },
        [organizationId],
    )

    const getQuestions = useCallback(
        async (searchTerm?: string) => {
            if (!organizationId) return []
            return await getQuestionsApi(organizationId, searchTerm)
        },
        [organizationId],
    )

    return useMemo(
        () => ({
            getApplicants,
            createInterview,
            scheduleExpertInterview,
            createVideoInterview,
            getQuestions,
        }),
        [
            getApplicants,
            createInterview,
            scheduleExpertInterview,
            createVideoInterview,
            getQuestions,
        ],
    )
}
